import React, { useContext, useState } from "react";

import styled from "@emotion/styled";

import { DataContext } from "../../../../../providers/GasDataProvider";
import pushToAnalytics from "../../../../../utils/analytics";
import NewInput from "../../../../shared/forms/NewInput";
import NewSelectInput from "../../../../shared/forms/NewSelectInput";
import Button from "../../../../shared/NewForms/Button";
import ConsumptionInput from "./ConsumptionInput";
import ZipcodeInput from "./ZipcodeInput";

const Form = styled.form`
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;
    width: 100%;
    margin-bottom: 20px;
    gap: 6px 12px;

    ${({ theme }) => theme.breakpoints.from.small.css`
        column-gap: 5%;
        justify-content: start;
    `}
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    margin-top: 20px;
    margin-bottom: 30px;
`;

const InputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 48%;

    ${({ theme }) => theme.breakpoints.from.small.css`
        width: 30%;
    `}
`;

const Calculator = () => {
    const dataContext = useContext(DataContext);
    const {
        zipcode,
        consumption,
        checkingForZipcode,
        showPlaceInput,
        placeList,
        place,
        showStreetInput,
        streetSelectList,
        selectedStreet,
        houseNum,
        showHouseNumberInput,
    } = dataContext.data;
    const {
        setZipcode,
        setConsumption,
        calculateStreet,
        setSelectedStreet,
        setHouseNum,
        setStreetSelectList,
        setResult,
        setPlaceList,
        setPlace,
        clearCheckout,
        getStreets,
        clearFormError,
        setFormErrors,
    } = dataContext.actions;
    const defaultPlaceLabel = {
        value: "Bitte wählen",
        label: "Bitte wählen",
        value2: {
            ortsname: "",
        },
    };

    const defaultStreetLabel = { value: "Bitte wählen", label: "Bitte wählen" };
    const [localZipcode, setLocalZipcode] = useState(zipcode);
    const [localConsumption, setLocalConsumption] = useState(consumption);
    const [mayCalculate, setMayCalculate] = useState(true);
    const [zipcodeError, setZipcodeError] = useState(false);
    const [consumptionError, setConsumptionError] = useState(false);

    const setDefaultCalculatorForm = () => {
        setHouseNum("");
        setSelectedStreet(defaultStreetLabel);
        setPlace(defaultPlaceLabel);
        setPlaceList(null);
        setStreetSelectList(null);
        setMayCalculate(true);
        setResult(null);
    };

    const onChangeZipcode = (zipcode) => {
        // setZipcodeIsValid(false)
        if (zipcode.length > 5) {
            setLocalZipcode("");
        } else {
            setLocalZipcode(zipcode);
            if (zipcode.length === 5) {
                // checkZipcode(event.target.value);
                setZipcode(zipcode);
                setDefaultCalculatorForm();
            }
            setFormErrors([]);
        }
    };

    const onChangePlace = (e) => {
        setPlace(e);
        setMayCalculate(true);
        setSelectedStreet(defaultStreetLabel);
        setStreetSelectList(null);
        getStreets(localZipcode, e);
        clearFormError("place");
    };

    const onChangeStreet = (e) => {
        setSelectedStreet(e);
        setMayCalculate(true);
        clearFormError("street");
    };

    const onChangeConsumption = (event) => {
        setLocalConsumption(event.target.value);
        setMayCalculate(true);
    };

    const onChangeLocalConsumptionIcon = (value) => {
        setLocalConsumption(value);
        setMayCalculate(true);
    };

    const onChangeHouseNumber = (e) => {
        setHouseNum(e.target.value);
        setMayCalculate(true);
        clearFormError("housenumber");
    };

    const onSubmit = (event) => {
        event.preventDefault();
        if (mayCalculate) {
            setZipcode(localZipcode);
            setConsumption(localConsumption);
            setMayCalculate(false);
            // Trigger a gtm event
            pushToAnalytics("gas:calculate", {
                zipcode: localZipcode,
                consumption: localConsumption,
            });
            clearCheckout();
            calculateStreet(localZipcode, localConsumption);
        }
    };

    return (
        <Form onSubmit={onSubmit}>
            <InputWrapper>
                <ZipcodeInput
                    setMayCalculate={setMayCalculate}
                    setZipcode={onChangeZipcode}
                    localZipcode={localZipcode}
                    zipcodeError={zipcodeError}
                    setZipcodeError={setZipcodeError}
                />
            </InputWrapper>
            <InputWrapper>
                <ConsumptionInput
                    localConsumption={localConsumption}
                    onChangeConsumption={onChangeConsumption}
                    setConsumptionError={setConsumptionError}
                />
            </InputWrapper>
            <InputWrapper>
                <NewSelectInput
                    name="place"
                    placeholder="Ort"
                    value={place}
                    onChange={onChangePlace}
                    errorClassName="is-light"
                    options={
                        Array.isArray(placeList) &&
                        placeList[0].value !== "Bitte wählen"
                            ? placeList
                            : []
                    }
                    noOptionsMessage={() => "Keinen Ort gefunden."}
                    isDisabled={!showPlaceInput}
                />
            </InputWrapper>
            <InputWrapper>
                <NewSelectInput
                    name="street"
                    placeholder="Straße"
                    value={selectedStreet}
                    onChange={onChangeStreet}
                    errorClassName="is-light"
                    options={
                        Array.isArray(streetSelectList) &&
                        streetSelectList[0].value !== "Bitte wählen"
                            ? streetSelectList
                            : []
                    }
                    isDisabled={!showStreetInput}
                    noOptionsMessage={() => "Keine Straße gefunden."}
                    theme={(theme) => {
                        return {
                            ...theme,
                            borderRadius: "6px",
                            colors: {
                                ...theme.colors,
                                text: "black",
                            },
                        };
                    }}
                    withoutWrapper
                />
            </InputWrapper>
            <InputWrapper>
                <NewInput
                    name="houseNumber"
                    placeholder="Hausnummer"
                    value={houseNum}
                    onChange={onChangeHouseNumber}
                    onError={() => setFormErrors(["housenumber"])}
                    onErrorSolved={() => clearFormError("housenumber")}
                    isDisabled={!showHouseNumberInput}
                    required
                />
            </InputWrapper>
            <ButtonWrapper>
                <Button
                    type="submit"
                    data-gtag="gas-calculate-advanced"
                    color={mayCalculate ? "redOutline" : "red"}
                >
                    Neu berechnen
                </Button>
            </ButtonWrapper>
        </Form>
    );
};

export default Calculator;
